import React, { useState, useEffect } from 'react';
import { Card, List, Typography, Button, Empty, Spin, Tag, Space } from 'antd';
import { CloseOutlined, MessageOutlined, ClockCircleOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Text, Paragraph } = Typography;

/**
 * Conversation History Component
 * Shows past conversations and allows replaying queries
 */
const ConversationHistory = ({ userId, sessionId, onClose, onSelectMessage }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [userId, sessionId]);

  const loadHistory = async () => {
    if (!sessionId) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get('/api/agent/conversation', {
        params: { userId, sessionId },
      });

      if (response.data.success) {
        setHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error loading conversation history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAgentColor = (agentType) => {
    const colors = {
      sales_analytics: 'blue',
      customer_insights: 'green',
      supervisor: 'purple',
    };
    return colors[agentType] || 'default';
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <MessageOutlined />
          <Text strong>Conversation History</Text>
        </Space>
      }
      extra={
        <Button type="text" icon={<CloseOutlined />} onClick={onClose} size="small" />
      }
      style={{ marginBottom: '16px' }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin />
        </div>
      ) : history.length === 0 ? (
        <Empty
          description="No conversation history yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <List
          size="small"
          dataSource={history}
          renderItem={(item) => (
            <List.Item
              style={{
                padding: '8px 0',
                cursor: item.role === 'user' ? 'pointer' : 'default',
                borderBottom: '1px solid #f0f0f0',
              }}
              onClick={() => {
                if (item.role === 'user') {
                  onSelectMessage(item.content);
                  onClose();
                }
              }}
            >
              <List.Item.Meta
                avatar={
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: item.role === 'user' ? '#52c41a' : '#1890ff',
                      marginTop: '6px',
                    }}
                  />
                }
                title={
                  <Space>
                    <Text strong style={{ fontSize: '12px' }}>
                      {item.role === 'user' ? 'You' : 'Assistant'}
                    </Text>
                    {item.agentType && (
                      <Tag color={getAgentColor(item.agentType)} style={{ fontSize: '10px' }}>
                        {item.agentType}
                      </Tag>
                    )}
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      <ClockCircleOutlined /> {formatTimestamp(item.timestamp)}
                    </Text>
                  </Space>
                }
                description={
                  <Paragraph
                    ellipsis={{ rows: 2 }}
                    style={{ fontSize: '12px', marginBottom: 0 }}
                  >
                    {item.content}
                  </Paragraph>
                }
              />
            </List.Item>
          )}
          style={{ maxHeight: '300px', overflowY: 'auto' }}
        />
      )}
    </Card>
  );
};

export default ConversationHistory;
